import { type ReactNode } from 'react'

import { cn } from '../lib/cn'

/**
 * Step indicator — ported from `FgStepIndicator` in `designs/system.jsx`
 * (~547), derived from node `522:830`.
 *
 * PORTED: "Step N" Medium on `--ink-1` + "of M" regular on `--ink-3`, then
 * equal segments 5px tall at `--r-2`. Upstream paints inactive segments as a
 * raw `#d9d9d9` bound to no variable; M16 mapped it to `--line-2` and that
 * mapping carries over.
 *
 * THE STEP COUNT COMES FROM THE PRODUCT, NOT THE FRAMES. The artboards
 * variously say 4, 2, none and 3. That disagreement is itself the evidence
 * that the frames are not the authority on flow (ADR-0038) — the bed flow has
 * three steps, so `total` defaults to 3 and callers pass what they actually
 * have. Do not add a step because a frame drew one.
 *
 * The segments are decorative: the state is carried by `role="progressbar"`
 * with `aria-valuenow`, plus the visible "Step N of M" text. Colour alone
 * never conveys which step you are on — after T02 the accent barely separates
 * from the neutral ramp in greyscale (measured in M17-T06).
 */
export interface StepIndicatorProps {
  /** 1-based index of the current step. */
  step?: number
  total?: number
  className?: string
}

export function StepIndicator({
  step = 1,
  total = 3,
  className,
}: StepIndicatorProps): ReactNode {
  const label = `Step ${step} of ${total}`
  return (
    <div className={cn('flex w-full flex-col gap-1', className)}>
      <p className="text-[16px] leading-6">
        <span className="font-medium text-ink-1">Step {step}</span>
        <span className="text-ink-3"> of {total}</span>
      </p>
      <div
        role="progressbar"
        aria-valuenow={step}
        aria-valuemin={1}
        aria-valuemax={total}
        aria-label={label}
        className="flex w-full gap-[5px]"
      >
        {Array.from({ length: total }, (_, i) => (
          <span
            key={i}
            aria-hidden="true"
            className={cn(
              'h-[5px] flex-1 rounded-r2',
              i < step ? 'bg-accent-ink' : 'bg-line-2',
            )}
          />
        ))}
      </div>
    </div>
  )
}
